import { useApi } from '@bumble/app/hooks/use-api';
import { Session } from '@bumble/app/hooks/use-account';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { apiError } from '@bumble/app/utils/api-error';

interface SwitchableAccount {
  account_id: string;
  account_domain: string;
  current: boolean;
}

interface SwitchAccountDialogProps {
  open: boolean;
  onClose: () => void;
  session: Session | null;
  authenticate: (session: Session, redirect: string) => void;
}

export const SwitchAccountDialog = ({ open, onClose, session, authenticate }: SwitchAccountDialogProps) => {
  const api = useApi();
  const [accounts, setAccounts] = useState<SwitchableAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api
      .getSwitchableAccountsApiV1SwitchableAccountsGet()
      .then(({ data }) => {
        const accounts = data as unknown as SwitchableAccount[];
        setAccounts(accounts);
        const nonCurrent = accounts.find((a) => !a.current);
        setSelectedAccountId(nonCurrent?.account_id ?? '');
      })
      .catch(apiError)
      .finally(() => setLoading(false));
  }, [open, api]);

  const handleSwitch = async () => {
    if (!selectedAccountId) return;
    setSwitching(true);
    try {
      const { data } = await api.switchAccountApiV1SwitchAccountPost({ account_id: selectedAccountId });
      const result = data as unknown as { access_token: string; session_type: string };
      // IAM username is the same across all accounts; the switch-account response doesn't include it
      authenticate(
        { access_token: result.access_token, username: session?.username ?? '', session_type: result.session_type },
        '/main-dashboard',
      );
      onClose();
    } catch (err: any) {
      apiError(err);
    } finally {
      setSwitching(false);
    }
  };

  const switchableAccounts = accounts.filter((a) => !a.current);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Switch Account</DialogTitle>
      <DialogContent>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading accounts…
          </Typography>
        ) : switchableAccounts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No other accounts available.
          </Typography>
        ) : (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel id="switch-account-label">Account</InputLabel>
            <Select
              labelId="switch-account-label"
              value={selectedAccountId}
              label="Account"
              onChange={(e: SelectChangeEvent) => setSelectedAccountId(e.target.value)}
            >
              {switchableAccounts.map((a) => (
                <MenuItem key={a.account_id} value={a.account_id}>
                  {a.account_domain}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSwitch}
          variant="contained"
          startIcon={<SyncAltIcon />}
          disabled={!selectedAccountId || switching || loading}
        >
          {switching ? 'Switching…' : 'Switch Account'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
